import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ScenarioAnalytics from "../ScenarioAnalytics";
import {
  getGame,
  saveGameSettings,
  saveCourseLessons,
  loadCourseVideo,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import { normalizeContentMode } from "../../utils/contentMode";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";
import ProjectTeamAccess from "../ProjectTeamAccess";
import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import MaxVideoLimitHint from "../MaxVideoLimitHint";
import "../trainer/trainer-editor.css";
import "./course-editor.css";

const { addMessage, showDialog } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyQuizQuestion(lessonId) {
  const id = `cq-${Date.now()}`;
  return {
    id,
    questionText: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

function normalizeLessonFromServer(lesson) {
  if (!lesson) return lesson;

  let quizQuestions = Array.isArray(lesson.quizQuestions)
    ? lesson.quizQuestions.map((question) => ({
        ...question,
        answers: [...(question.answers ?? [])],
      }))
    : [];

  if (!quizQuestions.length && lesson.quizEnabled) {
    const legacyText = lesson.quizQuestion?.trim();
    const legacyAnswers = lesson.quizAnswers ?? [];
    if (legacyText || legacyAnswers.length) {
      quizQuestions = [
        {
          id: `cq-${lesson.id}-legacy`,
          questionText: legacyText ?? "",
          answers: legacyAnswers.map((answer) => ({ ...answer })),
        },
      ];
    }
  }

  return {
    ...lesson,
    quizEnabled: Boolean(lesson.quizEnabled) || quizQuestions.length > 0,
    quizQuestions,
  };
}

function createEmptyLesson(order) {
  const id = `l-${Date.now()}`;
  return {
    id,
    order,
    title: "",
    contentMode: "text",
    contentText: "",
    hasVideo: false,
    screenpath: "",
    quizEnabled: false,
    quizQuestions: [],
  };
}

function formatLessonCount(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} уроков`;
  if (mod10 === 1) return `${n} урок`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} урока`;
  return `${n} уроков`;
}

function isQuizQuestionComplete(question) {
  if (!question?.questionText?.trim()) return false;
  const filledAnswers = (question.answers ?? []).filter((a) => a.label?.trim());
  if (!filledAnswers.length) return false;
  return filledAnswers.some((a) => a.isCorrect);
}

function isLessonComplete(lesson) {
  if (!lesson?.title?.trim()) return false;

  const mode = normalizeContentMode(lesson.contentMode, "text");
  if (mode === "video" && !lesson.hasVideo) return false;
  if (mode === "text" && !lesson.contentText?.trim()) return false;
  if (mode === "mixed" && !lesson.hasVideo && !lesson.contentText?.trim()) {
    return false;
  }

  if (!lesson.quizEnabled) return true;

  const questions = lesson.quizQuestions ?? [];
  if (!questions.length) return false;
  return questions.every(isQuizQuestionComplete);
}

export default function CourseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const [view, setView] = useState("editor");
  const [title, setTitle] = useState(game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [lessons, setLessons] = useState(
    (game?.courseLessons ?? []).map(normalizeLessonFromServer),
  );
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [availableMessengers, setAvailableMessengers] = useState(
    readAvailableMessengersFromSettings(game?.settings),
  );
  const [chatAccessGate, setChatAccessGate] = useState(
    readChatAccessGateFromSettings(game?.settings),
  );
  const [savingLessons, setSavingLessons] = useState(false);
  const [expandedQuizQuestionId, setExpandedQuizQuestionId] = useState(null);

  const lessonsSaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);
  const lastSavedLessonsRef = useRef(JSON.stringify(game?.courseLessons ?? []));
  const lastSavedSettingsRef = useRef("");
  const loadedGameIdRef = useRef(null);

  const sortedLessons = useMemo(
    () => [...lessons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [lessons],
  );

  const activeLesson = useMemo(
    () => lessons.find((l) => l.id === activeLessonId) ?? null,
    [lessons, activeLessonId],
  );

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  const buildSettingsSnapshot = useCallback(
    () =>
      JSON.stringify({
        title: title.trim(),
        description,
        settings,
        availableMessengers,
        chatAccessGate,
      }),
    [title, description, settings, availableMessengers, chatAccessGate],
  );

  // Sync local editor state only when opening another project — not on every Redux
  // game update (would reset fields while typing / after autosave).
  useEffect(() => {
    if (!game?._id) return;
    if (loadedGameIdRef.current === game._id) return;

    loadedGameIdRef.current = game._id;
    setTitle(game.title ?? "");
    setDescription(game.description ?? "");
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));
    setChatAccessGate(readChatAccessGateFromSettings(game.settings));
    setLessons((game.courseLessons ?? []).map(normalizeLessonFromServer));
    setActiveLessonId(null);
    lastSavedLessonsRef.current = JSON.stringify(game.courseLessons ?? []);
    lastSavedSettingsRef.current = JSON.stringify({
      title: (game.title ?? "").trim(),
      description: game.description ?? "",
      settings: {
        projectStatus: getProjectStatusFromSettings(game.settings),
        completion_text: game.settings?.completion_text ?? "",
        author_telegram: game.settings?.author_telegram ?? "",
        allow_replay: game.settings?.allow_replay !== false,
      },
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
      chatAccessGate: readChatAccessGateFromSettings(game.settings),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-init on game switch
  }, [game?._id]);

  // Expand first quiz question when the active lesson changes — not when lesson
  // content is edited (lessons updates would collapse/re-expand on every keystroke).
  useEffect(() => {
    if (!activeLessonId) {
      setExpandedQuizQuestionId(null);
      return;
    }

    const lesson = lessons.find((item) => item.id === activeLessonId);
    const firstQuestion = lesson?.quizQuestions?.[0];
    setExpandedQuizQuestionId(firstQuestion?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to lesson selection
  }, [activeLessonId]);

  const persistLessons = useCallback(
    async (lessonsToSave, { silent = false } = {}) => {
      if (!game?._id) return false;
      const trimmedTitle = title.trim();
      setSavingLessons(true);
      try {
        await dispatch(
          saveCourseLessons({
            gameId: game._id,
            courseTheme: trimmedTitle,
            lessons: lessonsToSave,
          }),
        ).unwrap();
        lastSavedLessonsRef.current = JSON.stringify(lessonsToSave);
        if (!silent) {
          dispatch(addMessage({ type: "info", message: "Уроки сохранены" }));
        }
        return true;
      } catch {
        dispatch(addMessage({ type: "error", message: "Не удалось сохранить уроки" }));
        return false;
      } finally {
        setSavingLessons(false);
      }
    },
    [dispatch, game?._id, title],
  );

  const persistSettings = useCallback(async () => {
    if (!game?._id) return;
    const trimmedTitle = title.trim();
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: trimmedTitle,
          description,
          program: trimmedTitle,
          category: "",
          courseTheme: trimmedTitle,
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "course",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
            ...chatAccessGate,
          }),
        }),
      ).unwrap();
      lastSavedSettingsRef.current = buildSettingsSnapshot();
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));
    }
  }, [
    dispatch,
    game?._id,
    game?.settings,
    title,
    description,
    settings,
    availableMessengers,
    chatAccessGate,
    buildSettingsSnapshot,
  ]);

  useEffect(() => {
    const serialized = JSON.stringify(lessons);
    if (serialized === lastSavedLessonsRef.current || !game?._id) return;

    clearTimeout(lessonsSaveTimerRef.current);
    lessonsSaveTimerRef.current = setTimeout(() => {
      persistLessons(lessons, { silent: true });
    }, 1200);

    return () => clearTimeout(lessonsSaveTimerRef.current);
  }, [lessons, game?._id, persistLessons]);

  useEffect(() => {
    const serialized = buildSettingsSnapshot();
    if (serialized === lastSavedSettingsRef.current || !game?._id) return;

    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      persistSettings();
    }, 800);

    return () => clearTimeout(settingsSaveTimerRef.current);
  }, [
    title,
    description,
    settings,
    availableMessengers,
    chatAccessGate,
    game?._id,
    buildSettingsSnapshot,
    persistSettings,
  ]);

  const updateActiveLesson = (patch) => {
    if (!activeLessonId) return;
    setLessons((prev) =>
      prev.map((l) => (l.id === activeLessonId ? { ...l, ...patch } : l)),
    );
  };

  const updateQuizQuestion = (questionId, patch) => {
    if (!activeLesson) return;
    updateActiveLesson({
      quizQuestions: activeLesson.quizQuestions.map((question) =>
        question.id === questionId ? { ...question, ...patch } : question,
      ),
    });
  };

  const updateQuizAnswer = (questionId, answerId, patch) => {
    if (!activeLesson) return;
    updateActiveLesson({
      quizQuestions: activeLesson.quizQuestions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.map((answer) =>
                answer.id === answerId ? { ...answer, ...patch } : answer,
              ),
            }
          : question,
      ),
    });
  };

  const addQuizQuestion = () => {
    if (!activeLesson) return;
    const next = createEmptyQuizQuestion(activeLesson.id);
    updateActiveLesson({
      quizEnabled: true,
      quizQuestions: [...(activeLesson.quizQuestions ?? []), next],
    });
    setExpandedQuizQuestionId(next.id);
  };

  const removeQuizQuestion = (questionId) => {
    if (!activeLesson) return;
    const nextQuestions = activeLesson.quizQuestions.filter(
      (question) => question.id !== questionId,
    );
    updateActiveLesson({
      quizQuestions: nextQuestions,
      quizEnabled: nextQuestions.length > 0,
    });
    setExpandedQuizQuestionId((current) => {
      if (current !== questionId) return current;
      return nextQuestions[0]?.id ?? null;
    });
  };

  const removeQuizAnswer = (questionId, answerId) => {
    if (!activeLesson) return;
    const question = activeLesson.quizQuestions.find((item) => item.id === questionId);
    if (!question || question.answers.length <= 2) return;

    updateActiveLesson({
      quizQuestions: activeLesson.quizQuestions.map((item) =>
        item.id === questionId
          ? {
              ...item,
              answers: item.answers.filter((answer) => answer.id !== answerId),
            }
          : item,
      ),
    });
  };

  const toggleQuizQuestionExpanded = (questionId) => {
    setExpandedQuizQuestionId((current) =>
      current === questionId ? null : questionId,
    );
  };

  const addQuizAnswer = (questionId) => {
    if (!activeLesson) return;
    const question = activeLesson.quizQuestions.find((item) => item.id === questionId);
    if (!question || question.answers.length >= 4) return;

    updateQuizQuestion(questionId, {
      answers: [
        ...question.answers,
        {
          id: `${questionId}-a${question.answers.length + 1}`,
          label: "",
          isCorrect: false,
        },
      ],
    });
  };

  const handleSaveLesson = async () => {
    clearTimeout(lessonsSaveTimerRef.current);
    await persistLessons(lessons);
  };

  const handleAddLesson = async () => {
    if (activeLesson && !isLessonComplete(activeLesson)) {
      dispatch(
        addMessage({
          type: "error",
          message:
            "Заполните название, материал урока и при необходимости вопросы по теме",
        }),
      );
      return;
    }

    clearTimeout(lessonsSaveTimerRef.current);
    const saved = await persistLessons(lessons);
    if (!saved && activeLesson) return;

    const next = createEmptyLesson(lessons.length);
    setLessons((prev) => [...prev, next]);
    setActiveLessonId(next.id);
  };

  const handleViewChange = async (nextView) => {
    if (view === "settings" && nextView !== "settings") {
      clearTimeout(settingsSaveTimerRef.current);
      await persistSettings();
    }
    if (view === "editor" && nextView !== "editor") {
      clearTimeout(lessonsSaveTimerRef.current);
      await persistLessons(lessons, { silent: true });
    }
    setView(nextView);
  };

  const handleDeleteLesson = (lessonId) => {
    setLessons((prev) =>
      prev
        .filter((l) => l.id !== lessonId)
        .map((l, index) => ({ ...l, order: index })),
    );
    if (activeLessonId === lessonId) {
      setActiveLessonId(null);
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeLesson || !user?.id || !game?._id) return;

    dispatch(
      loadCourseVideo({
        userId: user.id,
        gameId: game._id,
        lessonId: activeLesson.id,
        file,
      }),
    ).then((result) => {
      if (result.payload?.courseLessons) {
        const normalized = result.payload.courseLessons.map(normalizeLessonFromServer);
        lastSavedLessonsRef.current = JSON.stringify(normalized);
        setLessons(normalized);
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        }),
      );
    };
  };

  const handleChatAccessChange = (gatePatch) => {
    setChatAccessGate((current) => ({ ...current, ...gatePatch }));
  };

  const renderToolbar = (currentView) => (
    <div className="app-editor__toolbar">
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "editor" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<EditOutlinedIcon />}
        onClick={() => handleViewChange("editor")}
      >
        Редактор
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "settings" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<SettingsOutlinedIcon />}
        onClick={() => handleViewChange("settings")}
      >
        Настройки
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "analytics" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<AnalyticsOutlinedIcon />}
        onClick={() => setView("analytics")}
      >
        Аналитика
      </Button>
      <Button
        variant="contained"
        onClick={() => dispatch(showDialog("delete"))}
        color="error"
        size="small"
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
      >
        Удалить
      </Button>
    </div>
  );

  const renderSettingsPanel = () => (
    <>
      <section className="trainer-editor__card trainer-editor__card--settings">
        <h3>Настройки курса</h3>
        <TextField
          label="Название курса"
          size="small"
          fullWidth
          sx={fieldSx}
          value={title}
          onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}
        />
        <TextField
          label="Приветственный текст"
          size="small"
          fullWidth
          multiline
          minRows={3}
          sx={fieldSx}
          value={description}
          onChange={(e) =>
            setDescription(sanitizePlainText(e.target.value, 2000))
          }
        />
        <div className="trainer-editor__settings-row">
          <TextField
            select
            label="Статус"
            size="small"
            sx={{ ...fieldSx, flex: 1 }}
            value={settings.projectStatus}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                projectStatus: e.target.value,
              }))
            }
          >
            <MenuItem value="draft">Черновик</MenuItem>
            <MenuItem value="published">Опубликован</MenuItem>
            <MenuItem value="hidden">Скрыт</MenuItem>
          </TextField>
          <TextField
            label="Telegram HR"
            size="small"
            sx={{ ...fieldSx, flex: 1.4 }}
            value={settings.author_telegram}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                author_telegram: sanitizePlainText(e.target.value, 80),
              }))
            }
          />
        </div>
        <div className="trainer-editor__course-behavior">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.allow_replay}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    allow_replay: e.target.checked,
                  }))
                }
              />
            }
            label="Разрешить повторное прохождение курса"
          />
          <p className="trainer-editor__course-behavior-hint">
            {settings.allow_replay
              ? "Сотрудник сможет пройти курс заново после завершения."
              : "После завершения курс будет доступен только для просмотра прогресса — повторно пройти нельзя."}
          </p>
        </div>
        <div className="trainer-editor__poster-row">
          <InputFileUpload
            handleAddPoster={(event) => handlePoster(event, game._id)}
          />
          <span className="trainer-editor__hint">Обложка</span>
        </div>
      </section>
      <ProjectTeamAccess
        gameId={game._id}
        value={availableMessengers}
        onChange={setAvailableMessengers}
        chatAccessSettings={chatAccessGate}
        onChatAccessChange={handleChatAccessChange}
      />
    </>
  );

  if (view === "analytics") {
    return (
      <div className="trainer-editor trainer-editor--course trainer-editor--analytics">
        {renderToolbar("analytics")}
        <ScenarioAnalytics embedded />
      </div>
    );
  }

  return (
    <div className="trainer-editor trainer-editor--course">
      {renderToolbar(view)}
      <div className="trainer-editor__meta-bar">
        <span className="trainer-editor__meta-theme">
          Курс: <strong>{title.trim() || "Без названия"}</strong>
        </span>
        {view === "editor" ? (
          <span className="trainer-editor__meta-count">
            {formatLessonCount(sortedLessons.length)}
          </span>
        ) : null}
      </div>

      {view === "settings" ? (
        <div className="trainer-editor__workspace trainer-editor__workspace--settings">
          <div className="trainer-editor__settings-column">
            {renderSettingsPanel()}
          </div>
        </div>
      ) : (
        <div className="trainer-editor__workspace">
          <section className="trainer-editor__card trainer-editor__card--questions">
            <div className="trainer-editor__card-head">
              <div>
                <h3>Уроки курса</h3>
                <p className="trainer-editor__card-lead">
                  Сотрудники проходят уроки по порядку: сначала материал (текст
                  или видео), затем вопросы по теме — если они добавлены.
                </p>
              </div>
            </div>

            <div className="trainer-editor__questions-layout">
              <div className="trainer-editor__questions-nav">
                {!sortedLessons.length ? (
                  <p className="trainer-editor__empty">
                    Добавьте первый урок — сотрудники будут проходить их
                    последовательно в боте.
                  </p>
                ) : (
                  <ul className="trainer-editor__question-list">
                    {sortedLessons.map((lesson, index) => (
                      <li
                        key={lesson.id}
                        className={`trainer-editor__question-item${
                          activeLessonId === lesson.id
                            ? " trainer-editor__question-item--active"
                            : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="trainer-editor__question-btn"
                          onClick={() => setActiveLessonId(lesson.id)}
                        >
                          <span className="trainer-editor__question-num">
                            {index + 1}
                          </span>
                          {lesson.title?.trim() || "Без названия"}
                        </button>
                        <IconButton
                          size="small"
                          className="trainer-editor__delete-btn"
                          aria-label="Удалить урок"
                          onClick={() => handleDeleteLesson(lesson.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddLesson}
                  className="trainer-editor__add-btn"
                  disabled={savingLessons}
                >
                  Добавить урок
                </Button>
              </div>

              {activeLesson ? (
                <div className="trainer-editor__question-editor">
                  <TextField
                    label="Название урока"
                    size="small"
                    fullWidth
                    sx={fieldSx}
                    value={activeLesson.title}
                    onChange={(e) =>
                      updateActiveLesson({
                        title: sanitizePlainText(e.target.value, 200),
                      })
                    }
                  />

                  <span className="trainer-editor__label">Формат материала</span>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={normalizeContentMode(activeLesson.contentMode, "text")}
                    onChange={(_, value) => {
                      if (value) updateActiveLesson({ contentMode: value });
                    }}
                    className="trainer-editor__mode-toggle"
                  >
                    <ToggleButton value="text">
                      <TextFieldsOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Текст
                    </ToggleButton>
                    <ToggleButton value="video">
                      <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Видео
                    </ToggleButton>
                    <ToggleButton value="mixed">Текст + видео</ToggleButton>
                  </ToggleButtonGroup>

                  {(activeLesson.contentMode === "text" ||
                    activeLesson.contentMode === "mixed") && (
                    <TextField
                      label="Текст урока"
                      size="small"
                      fullWidth
                      multiline
                      minRows={4}
                      sx={fieldSx}
                      value={activeLesson.contentText}
                      onChange={(e) =>
                        updateActiveLesson({
                          contentText: sanitizePlainText(e.target.value, 10000),
                        })
                      }
                    />
                  )}

                  {(activeLesson.contentMode === "video" ||
                    activeLesson.contentMode === "mixed") && (
                    <div className="trainer-editor__media-row">
                      <InputFileUpload
                        handleAddPoster={handleVideoUpload}
                        label="Загрузить видео"
                        accept="video/*"
                        fullWidth
                      />
                      <span className="trainer-editor__hint">
                        {activeLesson.hasVideo ? "Видео загружено" : "Загрузить видео"}
                      </span>
                      <MaxVideoLimitHint availableMessengers={availableMessengers} />
                    </div>
                  )}

                  <div className="trainer-editor__quiz-block">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(activeLesson.quizEnabled)}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            if (enabled && !activeLesson.quizQuestions?.length) {
                              const firstQuestion = createEmptyQuizQuestion(
                                activeLesson.id,
                              );
                              updateActiveLesson({
                                quizEnabled: true,
                                quizQuestions: [firstQuestion],
                              });
                              setExpandedQuizQuestionId(firstQuestion.id);
                              return;
                            }
                            updateActiveLesson({
                              quizEnabled: enabled,
                              quizQuestions: enabled
                                ? activeLesson.quizQuestions
                                : activeLesson.quizQuestions,
                            });
                          }}
                        />
                      }
                      label="Вопросы по теме"
                    />

                    {activeLesson.quizEnabled ? (
                      <div className="trainer-editor__quiz-questions">
                        {(activeLesson.quizQuestions ?? []).map(
                          (question, questionIndex) => {
                            const isExpanded =
                              expandedQuizQuestionId === question.id;
                            const preview =
                              question.questionText?.trim() || "Без текста";

                            return (
                              <div
                                key={question.id}
                                className={`trainer-editor__quiz-question-card${
                                  isExpanded
                                    ? " trainer-editor__quiz-question-card--expanded"
                                    : ""
                                }`}
                              >
                                <div
                                  className="trainer-editor__quiz-question-head"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    toggleQuizQuestionExpanded(question.id)
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      toggleQuizQuestionExpanded(question.id);
                                    }
                                  }}
                                >
                                  <ExpandMoreIcon
                                    className="trainer-editor__quiz-question-toggle"
                                    fontSize="small"
                                  />
                                  <div className="trainer-editor__quiz-question-summary">
                                    <h4 className="trainer-editor__quiz-question-title">
                                      Вопрос {questionIndex + 1}
                                    </h4>
                                    {!isExpanded ? (
                                      <p className="trainer-editor__quiz-question-preview">
                                        {preview}
                                      </p>
                                    ) : null}
                                  </div>
                                  {(activeLesson.quizQuestions?.length ?? 0) >
                                  0 ? (
                                    <IconButton
                                      size="small"
                                      className="trainer-editor__delete-btn"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        removeQuizQuestion(question.id);
                                      }}
                                      aria-label="Удалить вопрос"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                </div>

                                {isExpanded ? (
                                  <div className="trainer-editor__quiz-question-body">
                                    <TextField
                                      label="Текст вопроса"
                                      size="small"
                                      fullWidth
                                      multiline
                                      minRows={2}
                                      sx={fieldSx}
                                      value={question.questionText}
                                      onChange={(e) =>
                                        updateQuizQuestion(question.id, {
                                          questionText: sanitizePlainText(
                                            e.target.value,
                                            2000,
                                          ),
                                        })
                                      }
                                    />

                                    <div className="trainer-editor__quiz-answers">
                                      {question.answers.map((answer) => (
                                        <div
                                          key={answer.id}
                                          className="trainer-editor__answer-row"
                                        >
                                          <TextField
                                            label="Вариант ответа"
                                            size="small"
                                            sx={{ ...fieldSx, flex: 1 }}
                                            value={answer.label}
                                            onChange={(e) =>
                                              updateQuizAnswer(
                                                question.id,
                                                answer.id,
                                                {
                                                  label: sanitizePlainText(
                                                    e.target.value,
                                                    100,
                                                  ),
                                                },
                                              )
                                            }
                                          />
                                          <FormControlLabel
                                            className="trainer-editor__answer-check"
                                            control={
                                              <Checkbox
                                                checked={Boolean(
                                                  answer.isCorrect,
                                                )}
                                                onChange={(e) =>
                                                  updateQuizAnswer(
                                                    question.id,
                                                    answer.id,
                                                    {
                                                      isCorrect:
                                                        e.target.checked,
                                                    },
                                                  )
                                                }
                                              />
                                            }
                                            label="Верный"
                                          />
                                          {question.answers.length > 2 ? (
                                            <IconButton
                                              size="small"
                                              className="trainer-editor__delete-btn"
                                              onClick={() =>
                                                removeQuizAnswer(
                                                  question.id,
                                                  answer.id,
                                                )
                                              }
                                              aria-label="Удалить вариант"
                                            >
                                              <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>

                                    {question.answers.length < 4 ? (
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          addQuizAnswer(question.id)
                                        }
                                      >
                                        Добавить вариант
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            );
                          },
                        )}

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={addQuizQuestion}
                          className="trainer-editor__add-quiz-btn"
                        >
                          Добавить вопрос
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <div className="trainer-editor__lesson-actions">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SaveOutlinedIcon />}
                      onClick={handleSaveLesson}
                      disabled={savingLessons}
                      className="trainer-editor__add-quiz-btn"
                    >
                      {savingLessons ? "Сохранение…" : "Сохранить урок"}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="trainer-editor__empty">
                  Выберите урок слева или добавьте новый.
                </p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
